import {
  createContext,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Cell, type CellState } from "./cell";
import { cn } from "../lib/utils";

export type ToastTone = "neutral" | "info" | "stalled" | "active" | "failing";
export type ToastTransition = "success" | "failure";

export interface ToastOptions {
  readonly id?: string;
  readonly message: ReactNode;
  readonly tone?: ToastTone;
  readonly durationMs?: number | null;
  readonly dismissLabel?: string;
}

export type ToastUpdate = Partial<Omit<ToastOptions, "id">>;

interface ToastRecord extends ToastOptions {
  readonly id: string;
  readonly revision: number;
}

export interface ToastController {
  show(options: ToastOptions): string;
  update(id: string, update: ToastUpdate): void;
  dismiss(id: string): void;
}

const TONE_PRESENTATION: Record<ToastTone, { readonly color: string; readonly cell: CellState }> = {
  neutral: { color: "var(--sqk-gray-lit)", cell: "tacked" },
  info: { color: "var(--sqk-blue)", cell: "working" },
  stalled: { color: "var(--sqk-orange)", cell: "tacked" },
  active: { color: "var(--sqk-green)", cell: "welded" },
  failing: { color: "var(--sqk-red)", cell: "error" },
};

const ToastContext = createContext<ToastController | null>(null);
let nextToastId = 0;

function toastId(): string {
  nextToastId += 1;
  return `sqk-toast-${nextToastId}`;
}

export function toastTransition(
  previous: ToastTone,
  next: ToastTone,
): ToastTransition | undefined {
  if (previous !== "stalled") return undefined;
  if (next === "active") return "success";
  if (next === "failing") return "failure";
  return undefined;
}

export function Toast({
  tone = "neutral",
  transition,
  children,
  className,
  role,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  readonly tone?: ToastTone;
  readonly transition?: ToastTransition | undefined;
}) {
  const presentation = TONE_PRESENTATION[tone];
  return (
    <div
      className={cn("sqk-toast", className)}
      aria-atomic="true"
      data-tone={tone}
      data-transition={transition}
      role={role ?? (tone === "failing" ? "alert" : "status")}
      {...props}
    >
      <Cell aria-hidden="true" hue={presentation.color} state={presentation.cell} />
      {children}
    </div>
  );
}

export function ToastViewport({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="region"
      aria-label="Notifications"
      className={cn("sqk-toast-viewport", className)}
      {...props}
    >
      {children}
    </div>
  );
}

function ManagedToast({
  toast,
  onDismiss,
}: {
  readonly toast: ToastRecord;
  readonly onDismiss: (id: string) => void;
}) {
  const tone = toast.tone ?? "neutral";
  const previousTone = useRef(tone);
  const transition = useMemo(
    () => toastTransition(previousTone.current, tone),
    [tone],
  );
  useEffect(() => {
    previousTone.current = tone;
  }, [tone]);

  useEffect(() => {
    if (toast.durationMs === null || toast.durationMs === undefined) return;
    const timer = window.setTimeout(() => onDismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(timer);
  }, [onDismiss, toast.durationMs, toast.id, toast.revision]);

  return (
    <Toast tone={tone} transition={transition}>
      <span className="sqk-toast__message">{toast.message}</span>
      <button
        aria-label={toast.dismissLabel ?? "Dismiss notification"}
        className="sqk-toast__dismiss"
        onClick={() => onDismiss(toast.id)}
        type="button"
      >
        ×
      </button>
    </Toast>
  );
}

export function ToastProvider({
  children,
  maxVisible = 4,
}: PropsWithChildren<{
  readonly maxVisible?: number;
}>) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const [mounted, setMounted] = useState(false);
  const revision = useRef(0);

  useEffect(() => setMounted(true), []);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((options: ToastOptions) => {
    const id = options.id ?? toastId();
    revision.current += 1;
    setToasts((current) => {
      const record: ToastRecord = {
        ...options,
        id,
        revision: revision.current,
      };
      const existing = current.findIndex((toast) => toast.id === id);
      const next = existing === -1
        ? [...current, record]
        : current.map((toast, index) => index === existing ? record : toast);
      return next.slice(-Math.max(1, maxVisible));
    });
    return id;
  }, [maxVisible]);

  const update = useCallback((id: string, patch: ToastUpdate) => {
    revision.current += 1;
    setToasts((current) => current.map((toast) => toast.id === id
      ? { ...toast, ...patch, id, revision: revision.current }
      : toast));
  }, []);

  const controller = useMemo<ToastController>(() => ({ dismiss, show, update }), [dismiss, show, update]);
  const viewport = (
    <ToastViewport>
      {toasts.map((toast) => (
        <ManagedToast key={toast.id} onDismiss={dismiss} toast={toast} />
      ))}
    </ToastViewport>
  );

  return (
    <ToastContext.Provider value={controller}>
      {children}
      {mounted ? createPortal(viewport, document.body) : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastController {
  const controller = useContext(ToastContext);
  if (controller === null) throw new Error("useToast must be used within ToastProvider");
  return controller;
}
